var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { Component, Model, Watch } from 'vue-property-decorator';
import PageHeader from '@/components/page-header/PageHeader';
import ReleaseRosterDialog from '@/views/RosterManagement/ReleaseRosterDialog';
import { defaultCalendarSettings, dayMap } from '@/support/CalendarDefaults';
import ErrorHandler from '@/support/ErrorHandler';
import { Teacher } from '@/models/Teacher';
import { Subject } from '@/models/Subject';
import { Room } from '@/models/Room';
import { Level } from '@/models/Level';
import { Group } from '@/models/Group';
import AsyncSelect from '@/components/async-select/AsyncSelect';
import GenerateRosterDialog from '@/views/RosterManagement/GenerateRosterDialog';
import { DateTime } from 'luxon';
import LoaderCard from '@/components/loader-card/LoaderCard';
import AddEventDialog from '@/components/mi-calendar/AddEventDialog';
import { renderCustomTimeslot } from '@/support/CalendarEventRenderFunctions';
import EditEventDialog from '@/views/Users/Teacher/EditEventDialog';
import { Model as BaseModel } from '@/models/Model';
import { User } from '@/models/User';
import { DateTimeslotSummary } from '@/models/DateTimeslotSummary';
import AbstractUrl from '@/support/AbstractUrl';
import { isArray, isEmpty } from 'lodash';
let Roster = class Roster extends AbstractUrl {
    constructor() {
        super(...arguments);
        this.fullBusinessHours = {
            daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
            startTime: '00:00',
            endTime: '00:00',
        };
        this.isReleasingRoster = false;
        this.isHardLocked = false;
        this.isGeneratingRosterDialog = false;
        this.isLoading = false;
        this.eventData = null;
        this.events = [];
        this.clearSelects = false;
        this.isUnsafeMode = false;
        this.isShowingEventsDialog = false;
        this.isEditingEvent = false;
        this.calendarOptionsUpdated = false;
        this.isEventEditable = false;
        this.hasWeekends = false;
        this.showSafeModeButton = true;
        this.subjectModel = new Subject;
        this.subjects = [];
        this.roomModel = new Room;
        this.room = null;
        this.teacherModel = new Teacher;
        this.teachers = [];
        this.levelModel = new Level;
        this.levels = [];
        this.groupModel = new Group().sort('name');
        this.groups = [];
        this.filters = {
            subjects: [],
            room: '',
            teachers: [],
            levels: [],
            groups: [],
        };
        this.selectedEventId = null;
        this.selectedEvent = null;
        this.currentWeekStart = '';
    }
    get eventFilter() {
        return {
            teachers: this.teachers,
            room: this.room,
            levels: this.levels,
            groups: this.groups,
            subjects: this.subjects,
        };
    }
    get weekStart() {
        const setting = this.$store.state.settings.find((setting) => {
            return setting.key === 'first_day_of_week';
        });
        if (setting) {
            this.hasWeekends = this.hasWeekendsInSchoolTimes() ? this.hasWeekendsInSchoolTimes() : ['sunday', 'saturday'].includes(setting.value);
            return dayMap[setting.value];
        }
        return 1;
    }
    get calendarOptions() {
        return {
            ...defaultCalendarSettings,
            ...{
                editable: false,
                buttonText: {
                    today: this.$t('rosterManagement.today'),
                },
                header: {
                    left: 'title',
                    right: 'prev, today, next',
                },
                firstDay: this.weekStart,
                weekends: this.hasWeekends,
                eventOverlap: false,
                businessHours: this.fullBusinessHours,
                eventConstraint: 'businessHours',
                selectConstraint: 'businessHours',
                eventClick: (selectionInfo) => {
                    const activityElement = selectionInfo.jsEvent.composedPath().find((element) => {
                        return element && element.className ? element.className.includes('teacher-activity') : false;
                    });
                    this.selectedEventId = selectionInfo.event.id;
                    let clickEventId = activityElement ? activityElement.className.split('activity teacher-activity').pop('') : '';
                    clickEventId = clickEventId.replace(/\s/g, '');
                    const start = DateTime.fromJSDate(selectionInfo.event.start).toMillis();
                    const lock = DateTime.fromSQL(this.hardLockDate || '').toMillis();
                    if (start <= lock) {
                        this.isHardLocked = true;
                    }
                    const indexedEvent = !!clickEventId && !!clickEventId.length ? selectionInfo.event.extendedProps.events.find((event) => event.id === clickEventId) : selectionInfo.event.extendedProps.events[0];
                    const event = new BaseModel().dataToModel(indexedEvent, 'Event');
                    if (event.levels !== undefined) {
                        event.levels.forEach((level, index) => {
                            event.levels[index] = new BaseModel().dataToModel(level, 'Level');
                        });
                        if (typeof event.room === 'object') {
                            event.room = new BaseModel().dataToModel(event.room, 'Room');
                        }
                        if (event.users !== undefined) {
                            event.users.forEach((user, index) => {
                                event.users[index] = new BaseModel().dataToModel(user, 'User');
                            });
                        }
                        if (event.subject !== undefined) {
                            event.subject = new BaseModel().dataToModel(event.subject, 'Subject');
                        }
                        if (event.subjects !== undefined) {
                            event.subjects.forEach((subject, index) => {
                                event.subjects[index] = new BaseModel().dataToModel(subject, 'Subject');
                            });
                        }
                        if (event.groups !== undefined) {
                            event.groups.forEach((group, index) => {
                                event.groups[index] = new BaseModel().dataToModel(group, 'User');
                            });
                        }
                    }
                    event.starts_at = DateTime.fromJSDate(selectionInfo.event.start).toFormat('dd-LL-yyyy HH:mm');
                    event.ends_at = DateTime.fromJSDate(selectionInfo.event.end).toFormat('dd-LL-yyyy HH:mm');
                    this.selectedEvent = event;
                    this.isEditingEvent = true;
                },
                eventRender: (eventInfo) => {
                    if (!eventInfo.event.id) {
                        return true;
                    }
                    // const type = this.filterType;
                    const activities = this.$tc('roster.activities', 2);
                    return renderCustomTimeslot(eventInfo, 'users', this.getEventClass(eventInfo), activities);
                },
                datesRender: (info) => {
                    const calendarStart = DateTime.fromJSDate(info.view.activeStart).toFormat('yyyy-LL-dd');
                    if (this.currentWeekStart !== calendarStart) {
                        this.currentWeekStart = calendarStart;
                        const calendarEnd = DateTime.fromJSDate(info.view.activeEnd).toFormat('yyyy-LL-dd');
                        this.fetchEvents(calendarStart, calendarEnd);
                    }
                },
            },
        };
    }
    get filterType() {
        if (this.teachers.length) {
            return 'teachers';
        }
        else if (this.room) {
            return 'rooms';
        }
        else if (this.levels.length) {
            return 'levels';
        }
        else if (this.groups.length) {
            return 'groups';
        }
        else if (this.subjects.length) {
            return 'subjects';
        }
        else {
            return null;
        }
    }
    get showClearButton() {
        if (this.teachers.length && this.teachers[0] === this.$store.state.Auth.id) {
            return false;
        }
        return this.teachers.length > 0 || this.subjects.length > 0 || this.levels.length > 0 || this.room || this.groups.length > 0;
    }
    get breadcrumbs() {
        return [{ name: 'sCoolPlanner', path: `/roster/${this.$store.state.Auth ? this.$store.state.Auth.id : ''}` }];
    }
    get hardLockDate() {
        const hard_lock = this.$store.state.settings.find((setting) => {
            return setting.key === 'hard_lock_date';
        });
        return hard_lock.value + ' 23:59:59';
    }
    /*
      - Subjects - this.subjects Subject[];
      - Room - this.room - string | null;
      - Teachers/users - this.users Teacher[];
      - levels - this.levels Level[];
      - groups this.groups Group[];
    */
    mounted() {
        const currentDayNumber = parseInt(DateTime.local().toFormat('c'));
        this.currentWeekStart = DateTime.local().minus({ days: currentDayNumber - 1 }).toFormat('yyyy-LL-dd');
        const end = DateTime.local().plus({ days: 7 - currentDayNumber }).toFormat('yyyy-LL-dd');
        this.teachers = [this.$store.state.Auth.id];
        this.fetchEvents(this.currentWeekStart, end);
        this.isLoading = true;
        if (this.$route.query && !isEmpty(this.$route.query)) {
            this.$nextTick(() => {
                this.preSetFilters(this.$route.query);
                this.preFillSelects(this.$route.query);
                this.isLoading = true;
            });
            return;
        }
    }
    fetchEvents(starts, ends) {
        this.events = [];
        new DateTimeslotSummary()
            .filter({ ...this.eventFilter, ...{ starts_at: starts, ends_at: ends } })
            .limit(100)
            .load()
            .then((response) => {
            const summary = new DateTimeslotSummary({ data: response.data.data, meta: response.data.meta });
            this.events = summary.calendarEvents;
            this.isLoading = false;
        })
            .catch((error) => {
            ErrorHandler.network(error);
        });
    }
    preSetFilters(filters) {
        if (filters.subjects) {
            this.subjects = isArray(filters.subjects) ? filters.subjects : [filters.subjects];
        }
        if (filters.room) {
            this.room = filters.room;
        }
        if (filters.levels) {
            this.levels = isArray(filters.levels) ? filters.levels : [filters.levels];
        }
        if (filters.groups) {
            this.groups = isArray(filters.groups) ? filters.groups : [filters.groups];
        }
        if (filters.teachers) {
            this.teachers = isArray(filters.teachers) ? filters.teachers : [filters.teachers];
        }
    }
    async preFillSelects(filters) {
        if (filters.subjects && this.$refs.subjectSelect) {
            this.preFillSubjectSelect(isArray(filters.subjects) ? filters.subjects : [filters.subjects]);
        }
        if (filters.room && this.$refs.roomSelect) {
            this.preFillRoomSelect(isArray(filters.room) ? filters.room : [filters.room]);
        }
        if (filters.levels && this.$refs.levelSelect) {
            this.preFillLevelSelect(isArray(filters.levels) ? filters.levels : [filters.levels]);
        }
        if (filters.groups && this.$refs.groupSelect) {
            this.preFillGroupSelect(isArray(filters.groups) ? filters.groups : [filters.groups]);
        }
        if (filters.teachers && this.$refs.userSelect) {
            this.preFillUserSelect(isArray(filters.teachers) ? filters.teachers : [filters.teachers]);
        }
    }
    async preFillRoomSelect(roomIds) {
        const rooms = await new Room()
            .filter('ids', roomIds)
            .all();
        this.$refs.roomSelect.setDefaultValue(rooms.map((room) => ({
            value: room.id,
            text: room.name,
        })), false);
    }
    async preFillSubjectSelect(subjectIds) {
        const subjects = await new Subject()
            .filter('ids', subjectIds)
            .all();
        this.$refs.subjectSelect.setDefaultValue(subjects.map((subject) => ({
            value: subject.id,
            text: subject.name,
        })), false);
    }
    async preFillLevelSelect(levelIds) {
        const levels = await new Level()
            .filter('ids', levelIds)
            .all();
        this.$refs.levelSelect.setDefaultValue(levels.map((level) => ({
            value: level.id,
            text: level.name,
        })), false);
    }
    async preFillGroupSelect(groupIds) {
        const groups = await new Group()
            .filter('ids', groupIds)
            .all();
        this.$refs.groupSelect.setDefaultValue(groups.map((group) => ({
            value: group.id,
            text: group.name,
        })), false);
    }
    async preFillUserSelect(userIds) {
        const user = await new Group()
            .filter('ids', userIds)
            .all();
        this.$refs.userSelect.setDefaultValue(user.map((user) => ({
            value: user.id,
            text: user.name,
        })), false);
    }
    hasWeekendsInSchoolTimes() {
        const saturday = this.$store.state.openingHours.find((hour) => {
            return hour.id === 'saturday';
        });
        const sunday = this.$store.state.openingHours.find((hour) => {
            return hour.id === 'sunday';
        });
        return sunday.starts_at !== '00:00:00' && sunday.ends_at !== '00:00:00' || saturday.starts_at !== '00:00:00' && saturday.ends_at !== '00:00:00';
    }
    viewCalendarEvent(selectionInfo) {
        //
    }
    isFiltered() {
        return this.teachers.length || this.subjects.length || this.room || this.levels.length || this.groups.length;
    }
    resetFilters() {
        this.teachers = [this.$store.state.Auth.id] || [];
        this.room = null;
        this.levels = [];
        this.groups = [];
        this.subjects = [];
        this.clearSelects = true;
        const micalendar = this.$refs.rosterCalendar;
        if (!micalendar || !micalendar.calendar) {
            return;
        }
        const starts = this.formatJSDate(micalendar.calendar.view.activeStart);
        const ends = this.formatJSDate(micalendar.calendar.view.activeEnd);
        this.isLoading = true;
        this.fetchEvents(starts, ends);
    }
    formatJSDate(date) {
        return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
    }
    isBeforeDate(checkDate, referenceDate) {
        if (checkDate.month < referenceDate.month) {
            return true;
        }
        if (checkDate.month === referenceDate.month && checkDate.day <= referenceDate.day) {
            return true;
        }
        return false;
    }
    dialogClosed() {
        this.isHardLocked = false;
    }
    dialogChanged(isShowing) {
        if (!isShowing) {
            this.isEventEditable = false;
        }
    }
    getEventClass(eventInfo) {
        const eventStartDate = DateTime.fromMillis(Date.parse(eventInfo.event.start));
        const hardLockDate = DateTime.fromFormat(this.hardLockDate || '', 'yyyy-LL-dd HH:mm:ss');
        if (eventStartDate.toMillis() <= hardLockDate.toMillis()) {
            return 'fc-hard_lock';
        }
        if (eventInfo.event.extendedProps.events[0] && eventInfo.event.extendedProps.events[0].subjects && eventInfo.event.extendedProps.events[0].subjects.length) {
            return 'fc-teacher';
        }
        return 'attention';
    }
    handleEventEditted() {
        const micalendar = this.$refs.rosterCalendar;
        if (!micalendar || !micalendar.calendar) {
            return;
        }
        const starts = this.formatJSDate(micalendar.calendar.view.activeStart);
        const ends = this.formatJSDate(micalendar.calendar.view.activeEnd);
        this.fetchEvents(starts, ends);
    }
    filterChanged(value) {
        if (!value || !value.length) {
            return;
        }
        const micalendar = this.$refs.rosterCalendar;
        if (!micalendar || !micalendar.calendar) {
            return;
        }
        const filters = { ...this.eventFilter };
        this.setQueryParams(filters);
        const starts = this.formatJSDate(micalendar.calendar.view.activeStart);
        const ends = this.formatJSDate(micalendar.calendar.view.activeEnd);
        this.isLoading = true;
        this.fetchEvents(starts, ends);
    }
    changedCurrentWeekStart() {
        this.isLoading = true;
    }
};
__decorate([
    Model('teacher', { default: () => new User() }),
    __metadata("design:type", typeof (_a = typeof User !== "undefined" && User) === "function" ? _a : Object)
], Roster.prototype, "user", void 0);
__decorate([
    Watch('isShowingEventsDialog'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], Roster.prototype, "dialogChanged", null);
__decorate([
    Watch('room'),
    Watch('teachers'),
    Watch('groups'),
    Watch('levels'),
    Watch('subjects'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Roster.prototype, "filterChanged", null);
__decorate([
    Watch('currentWeekStart'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Roster.prototype, "changedCurrentWeekStart", null);
Roster = __decorate([
    Component({
        components: {
            PageHeader,
            ReleaseRosterDialog,
            GenerateRosterDialog,
            AsyncSelect,
            LoaderCard,
            AddEventDialog,
            EditEventDialog,
        },
    })
], Roster);
export default Roster;
